import type { TFunction } from 'i18next';
import { Edit, IconButton } from 'qampo-ui-library';
import TrashIcon from '../../assets/icons/trash.svg?react';
import { Tag } from '../UI';
import type { TableColumn } from '../UI';
import type { StaffResponse } from '../../api/generated';
import { DAY_LABEL_KEYS, DAY_ORDER } from './staffScheduleConstants';
import styles from './StaffSettings.module.css';

interface StaffColumnHandlers {
  competenceNameById: Map<string, string>;
  onEdit: (staff: StaffResponse) => void;
  onDelete: (staff: StaffResponse) => void;
}

const formatPeriod = (t: TFunction, staff: StaffResponse): string => {
  const end = staff.endDate ?? t('config-staff-ongoing');
  return `${staff.startDate} – ${end}`;
};

const formatWorkingDays = (t: TFunction, staff: StaffResponse): string => {
  const weekSchedule = staff.availability?.weekSchedule ?? {};
  const labels = DAY_ORDER.filter((day) => weekSchedule[day]).map((day) =>
    t(DAY_LABEL_KEYS[day]).slice(0, 3)
  );
  return labels.length > 0 ? labels.join(', ') : t('config-staff-no-days');
};

export const getStaffColumns = (
  t: TFunction,
  { competenceNameById, onEdit, onDelete }: StaffColumnHandlers
): TableColumn<StaffResponse>[] => [
  {
    key: 'fullName',
    header: t('config-staff-col-name'),
    render: (staff) => <span className={styles.nameCell}>{staff.fullName}</span>
  },
  {
    key: 'competences',
    header: t('config-staff-col-competences'),
    render: (staff) =>
      staff.staffProfessionIds.length > 0 ? (
        <div className={styles.tagList}>
          {staff.staffProfessionIds.map((id) => (
            <Tag key={id} label={competenceNameById.get(id) ?? id} />
          ))}
        </div>
      ) : (
        <span className={styles.muted}>{t('config-staff-none')}</span>
      )
  },
  {
    key: 'workingDays',
    header: t('config-staff-col-schedule'),
    render: (staff) => <span>{formatWorkingDays(t, staff)}</span>
  },
  {
    key: 'period',
    header: t('config-staff-col-period'),
    width: 200,
    render: (staff) => <span>{formatPeriod(t, staff)}</span>
  },
  {
    key: 'actions',
    header: '',
    width: 100,
    headerAlign: 'center',
    render: (staff) => (
      <div className={styles.rowActions}>
        <span title={t('config-staff-edit')} aria-label={t('config-staff-edit')}>
          <IconButton
            size="small"
            icon={<Edit width={14} height={14} />}
            onClick={() => onEdit(staff)}
            classes={styles.actionBtn}
          />
        </span>
        <span title={t('config-staff-delete-title')} aria-label={t('config-staff-delete-title')}>
          <IconButton
            size="small"
            icon={<TrashIcon width={14} height={14} />}
            onClick={() => onDelete(staff)}
            classes={`${styles.actionBtn} ${styles.actionBtnDelete}`}
          />
        </span>
      </div>
    )
  }
];
